import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type {
  AccountClosureClosed,
  AccountClosureOpen,
  ClosureBlocker,
  CloseAccountResponse,
  ClosureRefundPlan,
  DormantBalance,
  PartyType,
} from "../../api/types";
import { useAuth, useSession } from "../../auth/AuthProvider";
import { homeFor } from "../../auth/pages";
import { CommandError } from "../../components/CommandError";
import { RejectionDetail } from "../../components/RejectionDetail";
import { Banner, Button, Card, Dialog, Input, Money, PageHeader, QueryView } from "../../components/ui";
import { SUPPORT_EMAIL } from "../../config";
import { formatLocal } from "../../lib/datetime";
import { saveTextFile } from "../../lib/download";
import { isZeroDecimal } from "../../lib/money";
import { canCloseAccount, canExportRecords, canReadClosure } from "../../lib/roles";
import { useIntentKey } from "../../lib/useIntentKey";

// Organization → Close account (G-30 advertiser, G-45 publisher; migration
// 0065; UX-DESIGN.md §2.6, §3.7).
//
//   GET  /v1/account/closure  owner | admin, also after closure (account_lifecycle.go:50)
//   POST /v1/account/closure  owner, step-up, {confirm: true} (:51, :199-225)
//   GET  /v1/account/export   owner | admin, step-up; 8 MiB cap (:52, :227-251)
//
// The blockers come from the server (_closure_blockers, 0065:1004-1178),
// each with its resolution action; this screen links each one to where it
// is resolved. The close button is enabled only when the server says
// can_close AND there are no blockers; the server refuses anyway (422
// closure_blocked, with the fresh list).

/** What the owner types to confirm (UX §2.6: "confirmation-gated", irreversible). */
export const CONFIRM_WORD = "CLOSE";

/** 8 << 20 (account_lifecycle.go:39). */
const EXPORT_MAX_MIB = 8;

function Amount({ value, currency }: { value?: string; currency?: string | null }) {
  if (!value) return null;
  if (currency) return <Money value={value} currency={currency} />;
  // Some kinds carry no currency (payout_in_flight, provisional_hold): the exact ledger figure.
  return <span className="mono">{value}</span>;
}

interface BlockerView {
  title: string;
  what: ReactNode;
  /** Where it's resolved in the portal. */
  link?: { to: string; label: string };
  /** Resolved only by VADS support, not self-service. */
  support?: boolean;
}

/** A party-agnostic path the server sent ("/campaigns/<id>") under this portal's home. */
function under(home: string, path: string): string {
  return path.startsWith("/") ? `${home}${path}` : `${home}/${path}`;
}

export function blockerView(b: ClosureBlocker, home: string): BlockerView {
  const serverLink = b.link ? under(home, b.link) : undefined;
  switch (b.kind) {
    // ---- advertiser ----
    case "active_campaign":
      return {
        title: `Live campaign: ${b.name ?? "unnamed"}`,
        what: "End it first, so you see exactly what's being ended. Closing never ends a live campaign on its own.",
        link: { to: serverLink ?? `${home}/campaigns/${b.id}`, label: "Open the campaign to end it" },
      };
    case "reservation":
      return {
        title: b.state === "disputed" ? "A delivery under dispute" : "A delivery still settling",
        what:
          b.resolution_action === "wait_for_dispute_decision"
            ? "Wait for the dispute on this delivery to be decided."
            : "Wait for this reserved spend to settle or be released.",
        link: b.campaign_id ? { to: `${home}/campaigns/${b.campaign_id}`, label: "Open the campaign" } : { to: `${home}/campaigns`, label: "Campaigns" },
      };
    case "dispute":
      if (b.dispute_type === "chargeback") {
        return {
          title: "An open chargeback",
          what: "Wait for your bank's chargeback on this deposit to be decided.",
          link: { to: `${home}/wallet`, label: "See deposits in Wallet" },
        };
      }
      return {
        title: "An open billing dispute",
        what: "Wait for the dispute to be decided.",
        link: b.statement_dispute_id
          ? { to: `${home}/disputes/${b.statement_dispute_id}`, label: "Open the dispute" }
          : { to: `${home}/disputes`, label: "Billing disputes" },
      };
    case "pending_deposit":
      return {
        title: "A deposit still pending",
        what: "Wait for the payment to settle or fail.",
        link: { to: `${home}/wallet`, label: "See deposits in Wallet" },
      };
    case "pending_refund":
      return {
        title: "A refund in progress",
        what: "Wait for the refund to finish.",
        link: { to: b.id ? `${home}/refunds/${b.id}` : `${home}/refunds`, label: "Open the refund" },
      };
    case "receivable":
      return { title: "An amount you owe", what: "Settle the amount owed first.", link: { to: `${home}/wallet`, label: "See the amount owed in Wallet" } };
    case "funds_held":
      return { title: "Funds on hold", what: "Wait for the hold to be released.", link: { to: `${home}/wallet`, label: "Wallet" } };
    case "pending_adjudication":
      return {
        title: "A case VADS is still deciding",
        what: "A case that may credit you back is waiting for VADS's decision. There's nothing to do but wait; you'll be notified.",
      };
    case "unrefundable_balance":
      return {
        title: "Balance no deposit can refund",
        what: "This part of your wallet can't go back to any original payment method, so it needs VADS support to return it.",
        support: true,
        link: { to: `${home}/wallet`, label: "Wallet" },
      };
    // ---- publisher ----
    case "recognized_unpaid":
      return {
        title: "Earnings not yet paid out",
        what:
          "Request a payout of your recognized balance first. (Anything still inside its 14-day hold, or below the payout minimum on its own, doesn't block closing — it's held on the account instead, not lost.)",
        link: { to: `${home}/payouts`, label: "Request a payout" },
      };
    case "pending_in_window":
      return {
        title: "Earnings still in their audit window",
        what: "Wait until they're recognized, then pay them out.",
        link: { to: `${home}/earnings`, label: "See pending earnings" },
      };
    case "compliance_held":
      return {
        title: "Earnings held for compliance review",
        what: `These funds need a compliance review that's handled by VADS support, not in this portal. Contact ${SUPPORT_EMAIL}; there's no self-service action.`,
        support: true,
      };
    case "open_dispute":
      return {
        title: "An open dispute",
        what: "Wait for the dispute to be decided.",
        link:
          b.dispute_type === "statement_line" && b.id
            ? { to: `${home}/disputes/${b.id}`, label: "Open the dispute" }
            : { to: `${home}/disputes`, label: "Disputes" },
      };
    case "payout_in_flight":
      return {
        title: "A payout in progress",
        what: "Wait for the payout to finish.",
        link: { to: serverLink ?? `${home}/payouts/${b.id}`, label: "Open the payout" },
      };
    case "provisional_hold":
      return {
        title: "A payout under review",
        what: "Wait for VADS to decide the provisional hold on this payout.",
        link: { to: b.payout_id ? `${home}/payouts/${b.payout_id}` : `${home}/payouts`, label: "Open the payout" },
      };
    case "publisher_debt":
      return {
        title: "An amount owed to VADS",
        what: b.id ? "Wait for the decision, or appeal it." : "Settle the amount owed, or appeal the case.",
        link: { to: serverLink ?? (b.id ? `${home}/debt-cases/${b.id}` : `${home}/debt-cases`), label: "Open the case" },
      };
    case "open_reservation":
      return {
        title: "An ad delivery still open on your sites",
        what: "Wait for it to settle; it could still earn an engagement.",
        link: { to: `${home}/earnings`, label: "Earnings" },
      };
    // ---- both ----
    case "ledger_balance":
      return {
        title: "Another balance on the account",
        what: "A balance that doesn't fit the other categories needs VADS support to resolve.",
        support: true,
      };
  }
}

export function CloseAccountPage() {
  const { me, partyType } = useSession();
  const home = homeFor(partyType);
  const [closed, setClosed] = useState<CloseAccountResponse | null>(null);
  return (
    <div className="stack-lg">
      <PageHeader
        title="Close account"
        description="Closing is permanent for this account. Your issued statements stay available read-only afterwards."
      />
      {closed ? (
        <ClosedResult r={closed} home={home} />
      ) : canReadClosure(me.roles) ? (
        <ClosureScreen home={home} partyType={partyType} onClosed={setClosed} />
      ) : (
        <Banner tone="info" title="Only an owner or admin can see this">
          <span data-testid="closure-role-note">
            An owner or admin can check what's blocking closure and download the organization's records. Only an owner can close the account.
          </span>
        </Banner>
      )}
    </div>
  );
}

function ClosureScreen({ home, partyType, onClosed }: { home: string; partyType: PartyType; onClosed: (r: CloseAccountResponse) => void }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["account-closure"], queryFn: () => ep.account.getClosure() });
  return (
    <QueryView query={q} loadingLabel="Checking what's left to resolve">
      {(c) =>
        c.state === "closed" ? (
          <ClosureRecord c={c} home={home} />
        ) : (
          <div className="stack-lg">
            <BlockersCard c={c} home={home} />
            {partyType === "advertiser" && c.refund_plan.length ? <RefundPlanCard plan={c.refund_plan} /> : null}
            {partyType === "publisher" && c.payout_hold_notice.length ? <PayoutHoldNoticeCard items={c.payout_hold_notice} /> : null}
            <ExportCard />
            <CloseCard c={c} partyType={partyType} onClosed={onClosed} />
          </div>
        )
      }
    </QueryView>
  );
}

function BlockersCard({ c, home }: { c: AccountClosureOpen; home: string }) {
  const counts = Object.entries(c.blocker_counts);
  const truncated = counts.some(([kind, n]) => (n ?? 0) > c.blockers.filter((b) => b.kind === kind).length);
  return (
    <Card title="Before you close" subtitle="Everything that has to be resolved first. Closure is refused while anything is listed here.">
      {c.blockers.length === 0 ? (
        <Banner tone="success" title="Nothing is blocking closure">
          Every balance, delivery and case is resolved.
        </Banner>
      ) : (
        <div className="stack">
          <Banner tone="warning" title={`${c.blockers.length === 1 ? "1 thing needs" : `${c.blockers.length} things need`} resolving first`}>
            Each item says what to do and links to where you do it.
          </Banner>
          <ul className="stack" aria-label="Closure blockers">
            {c.blockers.map((b, i) => {
              const v = blockerView(b, home);
              return (
                <li key={`${b.kind}-${b.id ?? b.account_type ?? i}-${b.currency ?? ""}`} data-blocker={b.kind} className="stack-sm">
                  <strong>{v.title}</strong>
                  {b.amount ? (
                    <span>
                      Amount: <Amount value={b.amount} currency={b.currency} />
                    </span>
                  ) : null}
                  <span>{v.what}</span>
                  {b.note ? <span className="small muted">{b.note}</span> : null}
                  {v.support ? (
                    <span className="small">
                      Needs support: contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                    </span>
                  ) : null}
                  {v.link ? (
                    <span>
                      <Link to={v.link.to}>{v.link.label}</Link>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {truncated ? <p className="xs muted">Only the first 25 of each kind are listed; resolve those and the rest appear.</p> : null}
        </div>
      )}
    </Card>
  );
}

function RefundPlanCard({ plan }: { plan: ClosureRefundPlan[] }) {
  return (
    <Card title="Your remaining wallet" subtitle="What happens to it when you close.">
      <ul className="stack-sm">
        {plan.map((p) => (
          <li key={p.currency}>
            <Money value={p.refund_total} currency={p.currency} /> is refunded to your original payment methods (whole cents, newest deposits
            first).
            {!isZeroDecimal(p.dust) ? (
              <>
                {" "}
                The remaining <span className="mono">{p.dust}</span> {p.currency.toUpperCase()} (less than a cent) is kept on record for you, not
                lost.
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Migration 0074: publisher earnings that don't block closing only because they're below the minimum payout or still inside their 14-day hold. */
function PayoutHoldNoticeCard({ items }: { items: AccountClosureOpen["payout_hold_notice"] }) {
  return (
    <Card title="Earnings that will stay held" subtitle="Below the minimum payout, or still inside the 14-day hold — this doesn't block closing.">
      <ul className="stack-sm">
        {items.map((n) => (
          <li key={n.currency}>
            <Money value={n.amount} currency={n.currency} /> stays on the account, held, once you close — not paid out (it's under the{" "}
            <Money value={n.minimum_payout_amount} currency={n.currency} /> minimum payout, or hasn't cleared its 14-day hold), and not lost.
            Recovering it after closing needs VADS support.
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ExportCard() {
  const { me, partyType } = useSession();
  const ep = useEndpoints();
  const m = useMutation({
    mutationFn: () => ep.account.exportRecords(),
    onSuccess: (r) => saveTextFile(r.text, r.filename ?? "vads-account-export.json", "application/json"),
  });
  if (!canExportRecords(me.roles)) return null;
  const tooLarge = m.error instanceof ApiError && m.error.code === "export_too_large" ? m.error : null;
  return (
    <Card title="Download your records" subtitle="Your own copy to keep, whatever happens to the account.">
      <div className="stack-sm">
        <p className="small">
          One JSON file with your statements and amendments, ledger postings, members (no passwords or keys), disputes, audit events and approvals, plus your campaigns, deposits and refunds or your
          sites, payouts and cases. It's produced in one go and can be at most {EXPORT_MAX_MIB} MiB; a larger account gets no file and needs support
          for a copy. You'll confirm it's you first.
        </p>
        {m.isError ? (
          <CommandError error={m.error}>
            {tooLarge && typeof tooLarge.detail?.size_bytes === "number" ? (
              <span className="small">
                Your records are {Math.ceil((tooLarge.detail.size_bytes / 1048576) * 10) / 10} MiB. Contact{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </span>
            ) : null}
            <RejectionDetail error={m.error} home={homeFor(partyType)} />
          </CommandError>
        ) : null}
        {m.isSuccess ? (
          <Banner tone="success" title="Download started" live="polite">
            {m.data.filename ?? "vads-account-export.json"}
          </Banner>
        ) : null}
        <div>
          <Button variant="secondary" onClick={() => m.mutate()} loading={m.isPending}>
            Download records (JSON)
          </Button>
        </div>
      </div>
    </Card>
  );
}

function whatHappens(partyType: PartyType): string[] {
  const shared = [
    "Every member is signed out, and the organization's open approvals and invitations are canceled.",
    "Issued statements stay available read-only: sign in with the same email to see them. Nothing else in the portal works afterwards.",
    "Nothing is deleted: records are kept for the legally required period.",
    "It can't be undone. You can sign up again, but this account doesn't reopen.",
  ];
  return partyType === "advertiser"
    ? [
        "Your wallet's whole cents are refunded to your original payment methods.",
        "Anything under one cent is kept as a dormant record with your contact details; returning it later needs fresh verification.",
        "Any campaign that never went live is ended.",
        ...shared,
      ]
    : [
        "Every site is closed and every API key revoked, so your servers stop getting ads.",
        "Your payout destination is retired, and a pending destination change is canceled.",
        "Earnings under one cent, still inside their 14-day hold, or below the payout minimum on their own are kept as a dormant record with your contact details; any later payout needs fresh identity and bank verification.",
        ...shared,
      ];
}

function CloseCard({ c, partyType, onClosed }: { c: AccountClosureOpen; partyType: PartyType; onClosed: (r: CloseAccountResponse) => void }) {
  const { me } = useSession();
  const { markAccountClosed } = useAuth();
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const isOwner = canCloseAccount(me.roles);
  const m = useMutation({
    mutationFn: () => ep.account.close({ idempotencyKey: intent.keyFor("close-account") }),
    onSuccess: (r) => {
      intent.reset();
      setOpen(false);
      onClosed(r);
      // Every session was revoked (0065:1358) and the party is closed: the
      // shell drops to its closed state and stops its background reads.
      markAccountClosed();
    },
    onError: (err) => {
      setOpen(false);
      if (err instanceof ApiError && err.code === "closure_blocked") void qc.invalidateQueries({ queryKey: ["account-closure"] });
    },
  });
  const clear = c.can_close && c.blockers.length === 0;

  return (
    <Card title="Close the account" subtitle="What happens when you close">
      <div className="stack">
        <ul className="stack-sm small">
          {whatHappens(partyType).map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        {m.isError ? (
          <CommandError error={m.error}>
            <RejectionDetail error={m.error} home={homeFor(partyType)} />
          </CommandError>
        ) : null}
        {isOwner ? (
          <>
            {!clear ? <p className="small">You can close once everything under "Before you close" is resolved.</p> : null}
            <div>
              <Button variant="danger" disabled={!clear} onClick={() => setOpen(true)} loading={m.isPending}>
                Close account…
              </Button>
            </div>
          </>
        ) : (
          <p className="small" data-testid="close-owner-note">
            Only an owner can close the account.
          </p>
        )}
      </div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        dismissible={!m.isPending}
        alert
        title="Close this account permanently?"
        description="This can't be undone. You'll confirm it's you before anything happens."
        actions={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={m.isPending}>
              Keep the account
            </Button>
            <Button variant="danger" disabled={typed !== CONFIRM_WORD || !clear} loading={m.isPending} onClick={() => m.mutate()}>
              Close account
            </Button>
          </>
        }
      >
        <Input
          label={`Type ${CONFIRM_WORD} to confirm`}
          value={typed}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
          hint="Download your records first if you want a copy."
        />
      </Dialog>
    </Card>
  );
}

function DormantList({ items }: { items: DormantBalance[] }) {
  if (!items.length) return null;
  return (
    <p className="small">
      Kept as a dormant record, not lost (a sub-cent remainder, or a publisher balance still under its hold or minimum payout), returned only
      after fresh verification:{" "}
      {items.map((d, i) => (
        <span key={`${d.account_type}-${d.currency}`}>
          {i ? ", " : ""}
          <span className="mono">{d.amount}</span> {d.currency.toUpperCase()}
        </span>
      ))}
      .
    </p>
  );
}

function ClosedResult({ r, home }: { r: CloseAccountResponse; home: string }) {
  const { logout } = useAuth();
  return (
    <Banner tone="info" title="The account is closed" live="polite">
      <div className="stack-sm" data-testid="closed-result">
        <span>Closed {formatLocal(r.closed_at)}.</span>
        {r.party_type === "advertiser" ? (
          <span>
            {r.refunds.length} refund{r.refunds.length === 1 ? "" : "s"} started back to your payment methods; {r.ended_campaigns} campaign
            {r.ended_campaigns === 1 ? "" : "s"} ended.
          </span>
        ) : (
          <span>
            {r.closed_sites} site{r.closed_sites === 1 ? "" : "s"} closed and {r.revoked_api_keys} API key{r.revoked_api_keys === 1 ? "" : "s"} revoked.
          </span>
        )}
        <DormantList items={r.dormant_balances} />
        <span>Every session was signed out, this one included. Sign in again to read your statements.</span>
        <span>
          <Button variant="primary" size="sm" onClick={() => void logout()}>
            Sign in again
          </Button>{" "}
          <Link to={`${home}/statements`}>Statements</Link>
        </span>
      </div>
    </Banner>
  );
}

function ClosureRecord({ c, home }: { c: AccountClosureClosed; home: string }) {
  return (
    <Card title="Closure record">
      <div className="stack-sm" data-testid="closure-record">
        <span>This account was closed on {formatLocal(c.closed_at)}. Issued statements stay available read-only.</span>
        {c.refunds.length ? (
          <ul className="stack-sm small" aria-label="Closure refunds">
            {c.refunds.map((r) => (
              <li key={r.refund_id}>
                Refund of <span className="mono">{r.amount}</span>: {r.state.replace(/_/g, " ")}
              </li>
            ))}
          </ul>
        ) : null}
        <DormantList items={c.dormant_balances} />
        <span>
          <Link to={`${home}/statements`}>Statements</Link>
        </span>
      </div>
    </Card>
  );
}
